<template>
  <div>
    <HrPageLayout
      title="My Performance Appraisals"
      subtitle="View and manage your appraisals"
      icon="📋"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'My Appraisals' }
      ]"
    >
      <template #header-actions>
        <button class="hr-btn hr-btn-primary" @click="startNewAppraisal">
          <i class="bx bx-plus"></i> Start New Appraisal
        </button>
      </template>

      <div class="my-appraisals-content">
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
            <p class="hr-metric-label">Total Appraisals</p>
            <h2 class="hr-metric-value">{{ statistics.totalAppraisals }}</h2>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-star"></i></div>
            <p class="hr-metric-label">Average Rating</p>
            <h2 class="hr-metric-value">{{ statistics.averageScore }}<small class="hr-text-muted">/5</small></h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
            <p class="hr-metric-label">Completed</p>
            <h2 class="hr-metric-value">{{ statistics.completedCount }}</h2>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <p class="hr-metric-label">Pending</p>
            <h2 class="hr-metric-value">{{ statistics.pendingCount }}</h2>
          </div>
        </div>

        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body hr-py-3">
            <div class="app-filters">
              <div class="hr-filter-group">
                <label class="hr-form-label">Year</label>
                <select v-model="filters.year" @change="loadAppraisals" class="hr-select">
                  <option value="">All Years</option>
                  <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Status</label>
                <select v-model="filters.status" @change="loadAppraisals" class="hr-select">
                  <option value="">All Status</option>
                  <option value="Draft">Draft</option>
                  <option value="Submitted">Submitted</option>
                  <option value="ReviewedByManager">Reviewed By Manager</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <button class="hr-btn hr-btn-outline hr-btn-secondary" @click="resetFilters">
                <i class="bx bx-refresh"></i> Reset
              </button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading appraisals...</p>
        </div>

        <div v-else-if="appraisals.length === 0" class="hr-chart-card hr-text-center hr-py-5">
          <i class="bx bx-file-blank" style="font-size: 48px; color: #64748b;"></i>
          <h5 class="hr-mt-4">No Appraisals Found</h5>
          <p class="hr-text-muted">You haven't created any appraisals yet.</p>
          <button class="hr-btn hr-btn-primary hr-mt-3" @click="startNewAppraisal">
            <i class="bx bx-plus"></i> Start Your First Appraisal
          </button>
        </div>

        <div v-else class="hr-chart-card">
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Appraisal Period</th>
                    <th>Year</th>
                    <th>Status</th>
                    <th>Overall Rating</th>
                    <th>Appraiser</th>
                    <th>Reviewer</th>
                    <th>Created Date</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="appraisal in appraisals" :key="appraisal.id">
                    <td>
                      <div class="hr-fw-medium">{{ appraisal.periodStart }} - {{ appraisal.periodEnd }}</div>
                    </td>
                    <td>
                      <span class="hr-badge hr-badge-primary">{{ appraisal.appraisalYear }}</span>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getStatusBadgeClass(appraisal.status)">{{ appraisal.status }}</span>
                    </td>
                    <td>
                      <div v-if="appraisal.finalOverallRating" class="hr-flex hr-align-center">
                        <i class="bx bx-star hr-text-warning hr-me-2"></i>
                        <div>
                          <strong>{{ parseFloat(appraisal.finalOverallRating).toFixed(2) }}</strong>/5
                          <div class="hr-progress-bar-wrap hr-mt-1">
                            <div class="hr-progress-bar hr-bg-warning" :style="{ width: (appraisal.finalOverallRating / 5 * 100) + '%' }"></div>
                          </div>
                        </div>
                      </div>
                      <span v-else class="hr-text-muted">Not Rated Yet</span>
                    </td>
                    <td>{{ appraisal.appraiserName || 'Not Assigned' }}</td>
                    <td>{{ appraisal.reviewerName || 'Not Assigned' }}</td>
                    <td>{{ appraisal.createdDate }}</td>
                    <td class="hr-text-end">
                      <div class="hr-action-btns">
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-info" @click="viewAppraisal(appraisal)" title="View Details">
                          <i class="bx bx-show"></i>
                        </button>
                        <button v-if="appraisal.status === 'Draft'" type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="editAppraisal(appraisal)" title="Continue Editing">
                          <i class="bx bx-edit"></i>
                        </button>
                        <button v-if="appraisal.status === 'Approved'" type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="downloadPDF(appraisal)" title="Download PDF">
                          <i class="bx bx-download"></i>
                        </button>
                        <button v-if="canDelete(appraisal)" type="button" class="hr-btn hr-btn-sm hr-btn-danger" @click="confirmDelete(appraisal)" title="Delete">
                          <i class="bx bx-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "MyAppraisals",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      appraisals: [],
      years: [],
      filters: { year: '', status: '' },
      statistics: {
        totalAppraisals: 0,
        averageScore: 0,
        completedCount: 0,
        pendingCount: 0
      }
    };
  },
  mounted() {
    this.loadAppraisals();
    this.generateYears();
  },
  methods: {
    async loadAppraisals() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getMyAppraisals");
        if (this.filters.year) data.append("year", this.filters.year);
        if (this.filters.status) data.append("status", this.filters.status);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.appraisals = res.data.data || [];
          this.calculateStatistics();
        } else {
          this.appraisals = [];
        }
      } catch (error) {
        console.error("Failed to load appraisals:", error);
        this.$toast?.error("Failed to load appraisals") || alert("Failed to load appraisals");
      } finally {
        this.loading = false;
      }
    },
    calculateStatistics() {
      const all = this.appraisals;
      this.statistics.totalAppraisals = all.length;
      const rated = all.filter(a => a.finalOverallRating);
      this.statistics.averageScore = rated.length > 0
        ? (rated.reduce((sum, a) => sum + parseFloat(a.finalOverallRating), 0) / rated.length).toFixed(2)
        : 0;
      this.statistics.completedCount = all.filter(a => a.status === 'Approved').length;
      this.statistics.pendingCount = all.filter(a =>
        a.status === 'Draft' || a.status === 'Submitted' || a.status === 'ReviewedByManager'
      ).length;
    },
    generateYears() {
      const currentYear = new Date().getFullYear();
      this.years = [];
      for (let i = 0; i < 5; i++) this.years.push(currentYear - i);
    },
    resetFilters() {
      this.filters.year = '';
      this.filters.status = '';
      this.loadAppraisals();
    },
    startNewAppraisal() {
      this.$router.push('/employee-self-assessment');
    },
    viewAppraisal(appraisal) {
      this.$router.push({ path: '/appraisal-details', query: { id: appraisal.id } });
    },
    editAppraisal(appraisal) {
      this.$router.push({ path: '/employee-self-assessment', query: { id: appraisal.id } });
    },
    downloadPDF(appraisal) {
      window.open(`${authUrl}?function=downloadAppraisalPDF&appraisalId=${appraisal.id}`, '_blank');
    },
    canDelete(appraisal) {
      return appraisal.status === 'Draft';
    },
    confirmDelete(appraisal) {
      if (confirm('Are you sure you want to delete this appraisal? This action cannot be undone.')) {
        this.deleteAppraisal(appraisal.id);
      }
    },
    async deleteAppraisal(appraisalId) {
      try {
        const data = new FormData();
        data.append("function", "deleteAppraisal");
        data.append("appraisalId", appraisalId);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success("Appraisal deleted successfully") || alert("Appraisal deleted successfully");
          this.loadAppraisals();
        } else {
          throw new Error(res?.data?.message || "Failed to delete appraisal");
        }
      } catch (error) {
        console.error("Failed to delete appraisal:", error);
        this.$toast?.error("Failed to delete appraisal") || alert("Failed to delete appraisal");
      }
    },
    getStatusBadgeClass(status) {
      const map = {
        'Draft': 'hr-badge-secondary',
        'Submitted': 'hr-badge-info',
        'ReviewedByManager': 'hr-badge-primary',
        'Approved': 'hr-badge-success',
        'Rejected': 'hr-badge-danger'
      };
      return map[status] || 'hr-badge-secondary';
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.app-filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.hr-text-end { text-align: right; }
.hr-fw-medium { font-weight: 600; }
.hr-flex { display: flex; }
.hr-align-center { align-items: center; }
.hr-me-2 { margin-right: 8px; }
.hr-mt-1 { margin-top: 4px; }
.hr-progress-bar-wrap { height: 5px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.hr-progress-bar { height: 100%; border-radius: 4px; transition: width 0.3s; }
.hr-bg-warning { background: #f59e0b; }
.hr-text-warning { color: #f59e0b; }
.hr-action-btns { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
.hr-btn-outline { background: transparent; border: 1px solid #e2e8f0; }
.hr-btn-outline:hover { background: #f8fafc; border-color: #94a3b8; }
</style>
